import React, { useState, useRef, useEffect, useMemo } from "react";
import { View, StyleSheet, Platform, Text, TouchableOpacity, ScrollView } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import LhButton from "../../components/LhButton";
import LHOverlay from "../../components/LHOverlay";
import CrossPlatformDropdown from "../../components/CrossPlatformDropdown";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../constants/fonts";
import {
  setSelectedAttendees,
  setSelectedShift,
  searchEmployeeAutocomplete,
  clearAutocompleteAttendees,
} from "../../store/slices/shiftHandOverSlice";
import { RootState } from "../../store";
import { AppDispatch } from "../../store";
import { formatUserName } from "../../utils/emailTouserName";
import { useTheme } from "../../theme";

interface ShiftHandOverBeginProps {
  isFilterOverlayVisible: boolean;
  closeFilterOverlay: () => void;
  onSave?: () => void;
}

const ShiftHandOverBegin: React.FC<ShiftHandOverBeginProps> = ({
  isFilterOverlayVisible,
  closeFilterOverlay,
  onSave,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch<AppDispatch>();
  const { 
    attendees, 
    selectedAttendees, 
    loading, 
    shiftOptions, 
    selectedShift,
    autocompleteAttendees,
    autocompleteLoading,
    autocompleteError
  } = useSelector((state: RootState) => state.shiftHandOver);

  // Get employees from appData as fallback
  const appDataEmployees = useSelector((state: RootState) => state.appData.employees);

  // Ref for debouncing autocomplete search
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // State for tracking search input
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Debounced search function
  const performAutocompleteSearch = (query: string) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only search if query has at least 3 characters
    if (query.length >= 3) {
      debounceTimerRef.current = setTimeout(() => {
        console.log("Performing autocomplete search for:", query);
        dispatch(searchEmployeeAutocomplete(query));
      }, 300); // 300ms debounce delay
    } else {
      dispatch(clearAutocompleteAttendees());
    }
  };

  // Combine static attendees with autocomplete results and appData employees
  const allAttendeeOptions = useMemo(() => {
    const staticOptions = attendees.map((attendee) => ({
      id: attendee.id,
      displayText: formatUserName(attendee.userPrincipalName),
      email: attendee.userPrincipalName,
    }));

    const autocompleteOptions = autocompleteAttendees.map((attendee) => ({
      id: attendee.id,
      displayText: formatUserName(attendee.userPrincipalName),
      email: attendee.userPrincipalName,
    }));

    // Transform appData employees to match the expected format
    // Only include appData employees when we have autocomplete results
    const appDataEmployeeOptions = (() => {
      // Only process appData employees if we have autocomplete results (after search is performed)
      if (autocompleteAttendees.length === 0) {
        console.log('No autocomplete results, skipping appData employees');
        return [];
      }

      console.log('Autocomplete results found, processing appData employees as additional fallback');

      // Handle both data structures for backward compatibility: {data: [...]} or directly [...]
      let employeesArray = [];
      if (Array.isArray(appDataEmployees)) {
        employeesArray = appDataEmployees;
      } else if (appDataEmployees && typeof appDataEmployees === 'object' && 'data' in appDataEmployees) {
        employeesArray = appDataEmployees.data || [];
      }

      // Get IDs, emails, and names from BOTH static attendees AND autocomplete options for filtering
      // We'll exclude employees that exist in either staticOptions OR autocompleteOptions
      const existingIds = new Set([
        ...staticOptions.map(option => option.id),
        ...autocompleteOptions.map(option => option.id)
      ]);
      
      const existingEmails = new Set([
        ...staticOptions.map(option => option.email?.toLowerCase()).filter(Boolean),
        ...autocompleteOptions.map(option => option.email?.toLowerCase()).filter(Boolean)
      ]);
      
      const existingNames = new Set([
        ...staticOptions.map(option => option.displayText?.toLowerCase()).filter(Boolean),
        ...autocompleteOptions.map(option => option.displayText?.toLowerCase()).filter(Boolean)
      ]);
      const transformedEmployees = employeesArray.map((employee) => {
        // Handle different employee data structures
        const firstName = employee.First_Name || employee.first_name || '';
        const lastName = employee.Last_Name || employee.last_name || '';
        const displayName = `${firstName} ${lastName}`.trim() || employee.name || 'Unknown Employee';
        const email = employee.email || employee.userPrincipalName || displayName || '';
        const id = employee.GPID || employee.gpId || '';

        return {
          id: id,
          displayText: displayName,
          email: email,
          _originalEmployee: employee // Keep reference for debugging
        };
      })
      .filter((emp) => {
        if (!emp.id) return false; // Filter out employees without valid IDs
        
        // Check for duplicates by ID, email, or name
        const isDuplicateById = existingIds.has(emp.id);
        const isDuplicateByEmail = emp.email && existingEmails.has(emp.email.toLowerCase());
        const isDuplicateByName = emp.displayText && existingNames.has(emp.displayText.toLowerCase());
        
        const isDuplicate = isDuplicateById || isDuplicateByEmail || isDuplicateByName;
        
        if (isDuplicate) {
          console.log('Filtering out duplicate employee:', {
            id: emp.id,
            displayText: emp.displayText,
            email: emp.email,
            reasons: {
              byId: isDuplicateById,
              byEmail: isDuplicateByEmail,
              byName: isDuplicateByName
            }
          });
        }
        
        return !isDuplicate;
      });

      console.log('Final appData employee options count:', transformedEmployees.length);
      return transformedEmployees.map(emp => ({
        id: emp.id,
        displayText: emp.displayText,
        email: emp.email
      }));
    })();

    // Combine all three sources - duplicates are already filtered out
    const combined = [...staticOptions, ...autocompleteOptions, ...appDataEmployeeOptions];
    
    console.log('Final combined attendee options:', {
      staticCount: staticOptions.length,
      autocompleteCount: autocompleteOptions.length,
      appDataCount: appDataEmployeeOptions.length,
      totalCount: combined.length
    });

    return combined;
  }, [attendees, autocompleteAttendees, appDataEmployees]);

  // Transform attendees for the dropdown (kept for backward compatibility)
  const attendeeOptions = attendees.map((attendee) => ({
    id: attendee.id,
    displayText: formatUserName(attendee.userPrincipalName),
    email: attendee.userPrincipalName,
  }));

  const handleAttendeesChange = (selected: any) => {
    // Convert from dropdown items back to attendees
    const selectedAttendeesList = Array.isArray(selected)
      ? selected.map((item) => ({
          id: item.id,
          displayName: item.displayText,
          userPrincipalName: item.email || "",
        }))
      : [];

    dispatch(setSelectedAttendees(selectedAttendeesList));
  };

  // Handle shift change
  const handleShiftChange = (selected: any) => {
    if (selected && typeof selected === "object") {
      dispatch(
        setSelectedShift({
          id: selected.id,
          displayText: selected.displayText,
        })
      );
    }
  };

  // Local state for shift validation error
  const [shiftError, setShiftError] = useState<string | null>(null);

  // Handle save action
  const handleSave = () => {
    if (!selectedShift) {
      setShiftError("Shift selection is required.");
      return;
    } else {
      setShiftError(null);
    }
    // Log the save action
    console.log("Saving attendees:", selectedAttendees);
    console.log("Selected shift:", selectedShift);

    // If onSave prop is provided, call it to navigate to the details view
    if (onSave) {
      onSave();
    } else {
      // Fallback to just closing the overlay if no onSave provided
      closeFilterOverlay();
    }
  };

  // Reset selectedShift to undefined when overlay is opened
  React.useEffect(() => {
    if (isFilterOverlayVisible) {
      // @ts-ignore: allow clearing selectedShift
      dispatch(setSelectedShift(undefined));
      // Clear autocomplete results when opening overlay
      dispatch(clearAutocompleteAttendees());
      setSearchQuery("");
    }
  }, [isFilterOverlayVisible, dispatch]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <LHOverlay
      visible={isFilterOverlayVisible}
      onDismiss={closeFilterOverlay}
      title="Shift Handover & Walk"
      size="medium"
    >
      <View style={styles.filterOverlayContainer}>
        {/* Dropdowns Row Container */}
        <View style={styles.dropdownsRow}>
          {/* Attendees Selection */}
          <View style={styles.dropdownColumn}>
            <View style={styles.labelRow}>
              <View style={styles.labelLeft}>
                <Text style={[styles.fieldLabel, isDarkMode && { color: colors.text.primary }]}>Additional Attendees</Text>
                <MaterialIcons
                  name="info-outline"
                  size={16}
                  color={isDarkMode ? colors.text.primary : "#6C757D"}
                  style={styles.infoIcon}
                />
              </View>
            </View>
            {loading ? (
              <Text style={isDarkMode ? { color: colors.text.primary } : undefined}>Loading attendees...</Text>
            ) : (
              <CrossPlatformDropdown
                label=""
                placeholder="Search and select attendees (type 3+ chars)"
                searchPlaceholder="Search attendees (3+ chars)..."
                childList={allAttendeeOptions}
                selectedValue={selectedAttendees.map((attendee) => ({
                  id: attendee.id,
                  displayText: attendee.displayName,
                  email: attendee.userPrincipalName,
                }))}
                setSelectedValue={handleAttendeesChange}
                selection="multi"
                size="medium"
                isClearable
                showCountOnMultiSelect
                multiSelectCountLabel="Attendees Selected"
                onSearchTextChange={(text: string) => {
                  setSearchQuery(text);
                  performAutocompleteSearch(text);
                }}
              />
            )}
            {autocompleteError && (
              <Text style={{ color: isDarkMode ? colors.text.primary : "red", marginTop: 4, fontSize: 12 }}>
                {autocompleteError}
              </Text>
            )}
            {autocompleteLoading && (
              <Text style={{ color: isDarkMode ? colors.text.primary : "#0066cc", marginTop: 4, fontSize: 12 }}>
                Searching employees...
              </Text>
            )}
            {/* Selected attendees chips */}
            {selectedAttendees.length > 0 && (
              <ScrollView
                horizontal={false}
                style={styles.chipsContainer}
                contentContainerStyle={styles.chipsContent}
              >
                {selectedAttendees.map((attendee) => (
                  <View key={attendee.id} style={styles.chip}>
                    <Text style={[styles.chipText, isDarkMode && { color: colors.text.inverse }]} numberOfLines={1}>
                      {attendee.displayName}
                    </Text>
                    <TouchableOpacity
                      onPress={() => {
                        const updated = selectedAttendees.filter(
                          (a) => a.id !== attendee.id
                        );
                        dispatch(setSelectedAttendees(updated));
                      }}
                      style={styles.chipRemove}
                      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                    >
                      <MaterialIcons name="close" size={12} color={colors.text.inverse} />
                    </TouchableOpacity>
                  </View>
                ))}
              </ScrollView>
            )}
          </View>

          {/* Shift Selection */}
          <View style={styles.dropdownColumn}>
            <View style={styles.labelRow}>
              <View style={styles.labelLeft}>
                <Text style={[styles.fieldLabel, isDarkMode && { color: colors.text.primary }]}>Shift</Text>
                <MaterialIcons
                  name="info-outline"
                  size={16}
                  color={isDarkMode ? colors.text.primary : "#6C757D"}
                  style={styles.infoIcon}
                />
              </View>
              <Text style={[styles.requiredText, isDarkMode && { color: colors.text.primary }]}>*Required</Text>
            </View>
            <CrossPlatformDropdown
              label=""
              placeholder="Select a shift"
              childList={shiftOptions}
              selectedValue={
                selectedShift
                  ? {
                      id: selectedShift.id,
                      displayText: selectedShift.displayText,
                    }
                  : null
              }
              setSelectedValue={handleShiftChange}
              selection="single"
              size="medium"
            />
            {shiftError && (
              <Text style={{ color: isDarkMode ? colors.text.primary : "red", marginTop: 4 }}>{shiftError}</Text>
            )}
          </View>
        </View>

        <View style={styles.filterOverlayButtons}>
          <LhButton
            testID="cancel-button"
            text="Cancel"
            variant="secondary"
            size="small"
            onPress={closeFilterOverlay}
            isLoading={false}
            disabled={false}
            style={{ ...styles.filterOverlayButton, ...styles.cancelButton }}
          />
          <LhButton
            testID="proceed-button"
            text="Proceed"
            variant="primary"
            size="small"
            onPress={handleSave}
            isLoading={false}
            disabled={false}
            style={styles.filterOverlayButton}
          />
        </View>
      </View>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  filterOverlayContainer: {
    // padding: 16,
  },
  filterOverlayButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    gap: 10,
  },
  filterOverlayButton: {
    width: Platform.OS === "web" ? "15%" : "40%",
    fontSize: 16,
  },
  cancelButton: {
    borderWidth: 0,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#333",
  },
  dropdownContainer: {
    marginBottom: 20,
  },
  dropdownsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    gap: 16,
  },
  dropdownColumn: {
    flex: 1,
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  labelLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#343A40",
    marginRight: 6,
    fontFamily: FontFamily.regular,
  },
  infoIcon: {
    marginTop: 2,
  },
  requiredText: {
    color: "#6C757D",
    fontSize: 12,
    fontFamily: FontFamily.regular,
  },
  chipsContainer: {
    maxHeight: 96,
    marginTop: 8,
  },
  chipsContent: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0066CC",
    borderRadius: 14,
    paddingVertical: 4,
    paddingLeft: 10,
    paddingRight: 6,
    maxWidth: 180,
  },
  chipText: {
    color: "#fff",
    fontSize: 12,
    fontFamily: FontFamily.regular,
    flexShrink: 1,
    marginRight: 4,
  },
  chipRemove: {
    backgroundColor: "rgba(0,0,0,0.2)",
    borderRadius: 10,
    padding: 2,
  },
});

export default ShiftHandOverBegin;
